import { useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { sellerService } from "@b2b/services";
import { useSellerProfile } from "./hooks/useSellerProfile";
import { HeroBanner } from "./components/HeroBanner";
import {
  PromotionalBanners,
  mergePromotionalAndStorefrontSliderBanners,
} from "./components/PromotionalBanners";
import { ProductsSection } from "./components/ProductsSection";
import { CompanyProfileSection } from "./components/CompanyProfileSection";
import { CertificationsSection } from "./components/CertificationsSection";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@b2b/ui-components";
import { getSellerDetailPath, replaceCanonicalPath } from "../../lib/productRoutes";
import { Loader2, Package, Building2, Award } from "lucide-react";

type SellerProduct = Record<string, unknown>;
type BannerItem = Record<string, unknown>;
type LooseObject = Record<string, unknown>;
type CategoryNode = {
  id?: string;
  name?: string;
  children?: CategoryNode[];
};

const toBannerList = (value: unknown): BannerItem[] => {
  if (Array.isArray(value)) return value as BannerItem[];
  if (
    value &&
    typeof value === "object" &&
    Array.isArray((value as { data?: unknown }).data)
  ) {
    return (value as { data: BannerItem[] }).data;
  }
  return [];
};

const getBannerOrder = (banner: BannerItem): number => {
  const raw = banner?.order;
  if (typeof raw === "number") return raw;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
};

/** Certificates from Branding & Media (profile_settings) live in businessProfileExtended.certificatesQuality */
function mergeStorefrontAndProfileCertifications(
  storefrontCerts: unknown,
  businessProfileExtended: unknown,
): unknown[] {
  const list: unknown[] = Array.isArray(storefrontCerts)
    ? [...storefrontCerts]
    : [];
  if (!businessProfileExtended || typeof businessProfileExtended !== "object")
    return list;
  const cq = (businessProfileExtended as Record<string, unknown>)
    .certificatesQuality;
  if (!Array.isArray(cq)) return list;
  for (const raw of cq) {
    if (!raw || typeof raw !== "object") continue;
    const o = raw as Record<string, unknown>;
    const title = typeof o.title === "string" ? o.title.trim() : "";
    const fileUrl = typeof o.fileUrl === "string" ? o.fileUrl.trim() : "";
    if (!title && !fileUrl) continue;
    list.push({
      name: title || "Certificate",
      imageUrl: fileUrl || undefined,
    });
  }
  return list;
}

interface SellerProfilePageProps {
  userRole?: "admin" | "seller" | "buyer";
  /** When true, renders as embedded content inside Bizcover India shell (no full-page wrapper). */
  embedded?: boolean;
  /** Optional outer className when embedded. */
  className?: string;
  /** Show chat CTA for any authenticated viewer (Bizcover seller detail). */
  showChatForAuthenticated?: boolean;
  /** Label for the company/about tab. Default: "About". */
  companyTabLabel?: string;
}

const SellerProfilePage = ({
  userRole,
  embedded = false,
  className = "",
  showChatForAuthenticated = false,
  companyTabLabel = "About",
}: SellerProfilePageProps) => {
  const { sellerId } = useParams<{ sellerId: string }>();
  const { currentBanner, setCurrentBanner, storefrontData, loading, error } =
    useSellerProfile(sellerId);
  const [categoryLookup, setCategoryLookup] = useState<Record<string, string>>(
    {},
  );
  const resolveIndustryLabel = useMemo(
    () => (value: string) => categoryLookup[value] || value,
    [categoryLookup],
  );

  useEffect(() => {
    if (!storefrontData || !sellerId) return;
    const companySlug = String(
      (storefrontData as any)?.company?.slug ||
        (storefrontData as any)?.storefront?.seoUrlSlug ||
        "",
    ).trim();
    if (companySlug && sellerId !== companySlug) {
      // Silent URL canonicalize — avoids remount/refetch flicker from navigate().
      replaceCanonicalPath(
        getSellerDetailPath({ id: sellerId, slug: companySlug }),
      );
    }
  }, [storefrontData, sellerId]);

  useEffect(() => {
    let active = true;
    sellerService
      .getCategories(true)
      .then((data: unknown) => {
        if (!active) return;
        const map: Record<string, string> = {};
        const walk = (node: CategoryNode) => {
          if (node.id && node.name) map[node.id] = node.name;
          if (Array.isArray(node.children)) {
            node.children.forEach((child) => walk(child));
          }
        };
        if (Array.isArray(data)) {
          (data as CategoryNode[]).forEach((node) => walk(node));
        }
        setCategoryLookup(map);
      })
      .catch(() => {
        if (!active) return;
        setCategoryLookup({});
      });

    return () => {
      active = false;
    };
  }, []);

  // Must be declared before any early return to satisfy Rules of Hooks.
  const featuredProductIds = useMemo<string[]>(() => {
    const list = (storefrontData?.storefront as Record<string, unknown> | undefined)
      ?.featuredProducts;
    if (!Array.isArray(list)) return [];
    return list
      .filter((item: unknown) => {
        if (!item || typeof item !== "object") return false;
        const row = item as Record<string, unknown>;
        return row.isActive !== false && row.productId != null;
      })
      .map((item: unknown) =>
        String((item as Record<string, unknown>).productId),
      );
  }, [storefrontData]);

  const loadingShellClass = embedded
    ? "flex items-center justify-center py-20"
    : "min-h-screen bg-background flex items-center justify-center";

  if (loading) {
    return (
      <div className={loadingShellClass}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading storefront...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={loadingShellClass}>
        <div className="text-center">
          <p className="text-destructive mb-4">
            {error || "Failed to load storefront"}
          </p>
        </div>
      </div>
    );
  }

  if (!storefrontData) {
    return (
      <div className={loadingShellClass}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading storefront data...</p>
        </div>
      </div>
    );
  }

  const {
    company,
    storefront,
    banners: rawBanners,
    promotionalBanners,
    seller,
    products = [] as SellerProduct[],
    testimonials,
    rating,
    reviewCount,
    membershipPlanName,
  } = storefrontData;
  const sellerObj = (seller || {}) as LooseObject;
  const sellerCompanyObj = (sellerObj.company as LooseObject) || {};
  const mergedCompany: LooseObject = {
    ...((storefront?.company as LooseObject) || {}),
    ...sellerCompanyObj,
    ...((company as LooseObject) || {}),
  };
  mergedCompany.industryTypes =
    sellerObj.industryTypes ||
    sellerCompanyObj.industryTypes ||
    mergedCompany.industryTypes;
  mergedCompany.industry =
    sellerObj.industry || sellerCompanyObj.industry || mergedCompany.industry;
  mergedCompany.businessProfileExtended =
    sellerCompanyObj.businessProfileExtended ||
    mergedCompany.businessProfileExtended;

  const fromExtended =
    (mergedCompany?.businessProfileExtended as LooseObject) || {};
  const fromStorefrontBiz = (storefront?.businessInfo as LooseObject) || {};
  const mergedBusinessInfo: LooseObject = {
    ...fromExtended,
    ...fromStorefrontBiz,
  };
  const fillBiz = (key: string, value: unknown) => {
    if (value === undefined || value === null || value === "") return;
    const cur = mergedBusinessInfo[key];
    if (cur === undefined || cur === null || cur === "") {
      mergedBusinessInfo[key] = value;
    }
  };
  fillBiz("factorySize", mergedCompany.factorySize);
  if (!mergedBusinessInfo.factorySize) {
    fillBiz("factorySize", mergedCompany.totalFactorySize);
  }
  fillBiz("totalFactorySize", mergedCompany.totalFactorySize);
  fillBiz("productionCapacity", mergedCompany.productionCapacity);
  fillBiz("employeeCount", mergedCompany.employeeCount);
  fillBiz("businessType", mergedCompany.businessType);
  fillBiz("companySize", mergedCompany.companySize);
  fillBiz("foundedYear", mergedCompany.foundedYear);
  fillBiz("businessLicense", mergedCompany.businessLicense);
  fillBiz("registrationNumber", mergedCompany.registrationNumber);
  fillBiz("taxId", mergedCompany.taxId);
  fillBiz("qualityCertifications", mergedCompany.qualityCertifications);
  fillBiz("legalBusinessName", mergedCompany.legalBusinessName);
  fillBiz("businessName", mergedCompany.businessName);
  fillBiz("legalStatus", mergedCompany.legalStatus);
  fillBiz("documentType", mergedCompany.documentType);
  fillBiz("annualTurnover", mergedCompany.annualTurnover);
  fillBiz("mainMarkets", mergedCompany.mainMarkets);
  fillBiz("shipmentModes", mergedCompany.shipmentModes);
  fillBiz("acceptedPaymentCurrency", mergedCompany.acceptedPaymentCurrency);
  fillBiz("paymentMethods", mergedCompany.paymentMethods);
  fillBiz("paymentTerms", mergedCompany.paymentTerms);
  fillBiz("exportPercentage", mergedCompany.exportPercentage);
  fillBiz("languagesSpoken", mergedCompany.languagesSpoken);
  fillBiz("nearestPort", mergedCompany.nearestPort);
  fillBiz("averageLoadTimeValue", mergedCompany.averageLoadTimeValue);
  fillBiz("averageLoadTimeUnit", mergedCompany.averageLoadTimeUnit);
  fillBiz("minOrderValueCurrency", mergedCompany.minOrderValueCurrency);
  fillBiz("minOrderValueAmount", mergedCompany.minOrderValueAmount);
  fillBiz("acceptedDeliveryTerms", mergedCompany.acceptedDeliveryTerms);
  fillBiz("contractManufacturing", mergedCompany.contractManufacturing);
  fillBiz("infrastructureOverview", mergedCompany.infrastructureOverview);
  fillBiz("productionLinesCount", mergedCompany.productionLinesCount);
  fillBiz("annualOutputValue", mergedCompany.annualOutputValue);
  fillBiz("qcStaffCount", mergedCompany.qcStaffCount);
  fillBiz("rndStaffCount", mergedCompany.rndStaffCount);
  fillBiz("productionFacilities", mergedCompany.productionFacilities);
  fillBiz("warehouseStorageArea", mergedCompany.warehouseStorageArea);
  fillBiz("companyVideoUrl", mergedCompany.companyVideoUrl);
  fillBiz("facebookUrl", mergedCompany.facebookUrl);
  fillBiz("linkedinUrl", mergedCompany.linkedinUrl);
  fillBiz("twitterUrl", mergedCompany.twitterUrl);
  fillBiz("youtubeUrl", mergedCompany.youtubeUrl);
  fillBiz("instagramUrl", mergedCompany.instagramUrl);
  fillBiz("website", mergedCompany.website);

  const storefrontBanners = toBannerList(rawBanners).sort(
    (a, b) => getBannerOrder(a) - getBannerOrder(b),
  );
  const sellerTopBanner =
    storefrontBanners.find((banner) => getBannerOrder(banner) === 0) ||
    storefrontBanners[0];
  const sellerTopBannerImage =
    sellerTopBanner && typeof sellerTopBanner.imageUrl === "string"
      ? sellerTopBanner.imageUrl
      : "";
  const sliderFromStorefront = storefrontBanners.filter(
    (banner) => banner !== sellerTopBanner && getBannerOrder(banner) >= 1,
  );
  const promoList = toBannerList(promotionalBanners);
  const mediaItems = mergePromotionalAndStorefrontSliderBanners(
    promoList,
    sliderFromStorefront,
  );

  const mergedCertifications = mergeStorefrontAndProfileCertifications(
    storefront?.certifications,
    mergedCompany.businessProfileExtended,
  );

  const hasHeroBanner = Boolean(sellerTopBannerImage);
  const hasPromoCarousel = mediaItems.length > 0;

  const resolvedSellerId = String(sellerObj.id || sellerId || "");
  const resolvedMembershipPlan = String(membershipPlanName || "").trim();
  const locationDisplay = [
    mergedCompany?.city,
    mergedCompany?.state,
    mergedCompany?.country,
  ]
    .filter(
      (value): value is string =>
        typeof value === "string" && value.trim().length > 0,
    )
    .join(", ");
  const outerClass = embedded
    ? `bg-transparent ${className}`.trim()
    : "min-h-screen bg-background";
  const cardClass = embedded
    ? "overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
    : "";
  const containerClass = embedded
    ? "w-full px-4 sm:px-6"
    : "container mx-auto px-4";
  const bannerSectionClass = embedded
    ? "border-b border-gray-100 px-4 py-4 sm:px-6 sm:py-6"
    : "border-b border-gray-100/80 bg-gradient-to-b from-slate-50 to-white py-5 sm:py-8";
  const bannerInnerClass = embedded ? "w-full" : containerClass;

  const heroFigure = sellerTopBannerImage ? (
    <figure className="group m-0 overflow-hidden rounded-2xl border border-gray-200/90 bg-slate-100 shadow-[0_20px_50px_-18px_rgba(0,0,0,0.18)] ring-1 ring-black/[0.05]">
      <div className="relative aspect-[5/3] w-full sm:aspect-[2.4/1] md:aspect-[2.75/1] lg:aspect-[3/1]">
        <img
          src={sellerTopBannerImage}
          alt={`${String(mergedCompany?.name || "Seller").trim() || "Seller"} — storefront banner`}
          className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-500 ease-out group-hover:scale-[1.01] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
          loading="eager"
          decoding="async"
          sizes="(max-width: 1536px) 100vw, 1536px"
        />
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-black/20 to-transparent sm:h-16"
          aria-hidden
        />
      </div>
    </figure>
  ) : null;

  const topHeroSection =
    hasHeroBanner && heroFigure ? (
      <section className={bannerSectionClass} aria-label="Storefront banner">
        <div className={bannerInnerClass}>{heroFigure}</div>
      </section>
    ) : null;

  const slideBannerSection = hasPromoCarousel ? (
    <PromotionalBanners
      banners={mediaItems}
      currentBanner={currentBanner}
      setCurrentBanner={setCurrentBanner}
      layout="full"
      embedded={embedded}
    />
  ) : null;

  return (
    <div className={outerClass}>
      <div className={cardClass}>
        {topHeroSection}

        <HeroBanner
          embedded={embedded}
          companyName={String(mergedCompany?.name || "")}
          logoUrl={
            String(storefront?.logoUrl || mergedCompany?.logo || "") ||
            undefined
          }
          location={locationDisplay}
          company={mergedCompany}
          businessInfo={mergedBusinessInfo}
          storefront={{ ...storefront, banners: storefrontBanners }}
          sellerId={resolvedSellerId}
          userRole={userRole}
          showChatForAuthenticated={showChatForAuthenticated}
          rating={typeof rating === "number" ? rating : null}
          reviewCount={typeof reviewCount === "number" ? reviewCount : 0}
          membershipPlan={resolvedMembershipPlan || null}
        />

        {slideBannerSection}

        <div className={`${containerClass} pb-4 pt-2 sm:pb-6 sm:pt-4 md:pb-8`}>
          <Tabs defaultValue="products" className="w-full">
            <div className="sticky top-0 z-20 -mx-4 overflow-x-auto border-b border-gray-200 bg-white px-4 scrollbar-hide sm:mx-0 sm:px-0">
              <TabsList className="mb-0 grid h-12 w-full min-w-[320px] grid-cols-3 rounded-none bg-white sm:h-14 sm:min-w-0">
                <TabsTrigger
                  value="products"
                  className="data-[state=active]:border-b-2 data-[state=active]:border-[#FF6A00] data-[state=active]:text-[#FF6A00] data-[state=active]:bg-transparent rounded-none text-xs sm:text-sm min-h-[44px] transition-all duration-200 hover:text-[#FF6A00]/80"
                >
                  <Package className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                  Products
                </TabsTrigger>
                <TabsTrigger
                  value="company"
                  className="data-[state=active]:border-b-2 data-[state=active]:border-[#FF6A00] data-[state=active]:text-[#FF6A00] data-[state=active]:bg-transparent rounded-none text-xs sm:text-sm min-h-[44px] transition-all duration-200 hover:text-[#FF6A00]/80"
                >
                  <Building2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                  {companyTabLabel}
                </TabsTrigger>
                <TabsTrigger
                  value="certifications"
                  className="data-[state=active]:border-b-2 data-[state=active]:border-[#FF6A00] data-[state=active]:text-[#FF6A00] data-[state=active]:bg-transparent rounded-none text-xs sm:text-sm min-h-[44px] transition-all duration-200 hover:text-[#FF6A00]/80"
                >
                  <Award className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Certifications</span>
                  <span className="sm:hidden">Certs</span>
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent
              value="products"
              className="mt-4 animate-fade-in sm:mt-6"
            >
              <ProductsSection
                sellerId={resolvedSellerId}
                featuredProductIds={featuredProductIds}
                products={products}
                supplierName={String(mergedCompany?.name || "")}
              />
            </TabsContent>

            <TabsContent
              value="company"
              className="mt-4 animate-fade-in sm:mt-6"
            >
              <CompanyProfileSection
                company={mergedCompany}
                storefront={storefront}
                testimonials={testimonials}
                seller={seller}
                businessInfo={mergedBusinessInfo}
                certifications={mergedCertifications}
                resolveIndustryLabel={resolveIndustryLabel}
                userRole={userRole}
                showChatForAuthenticated={showChatForAuthenticated}
              />
            </TabsContent>

            <TabsContent
              value="certifications"
              className="mt-4 animate-fade-in sm:mt-6"
            >
              <CertificationsSection
                certifications={mergedCertifications}
                businessInfo={mergedBusinessInfo}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default SellerProfilePage;
